import { claimIdentity } from '../../../webmodule-common/other/api/user-security';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { flushCache } from '../cache-impl/cache-registry';
import { getApiFactory } from '../../api/api-injector';
import { getCurrentUser, setUserClaims } from '../../../webmodule-common/other/api/current-user';
import {
  GlobalSupplierReference,
  ResultGetAllFranchiseeDetails
} from '../../api/supplier-api-interface-franchiseenetwork';
import { MultiPromise } from '../../../webmodule-common/other/general/multicast-promise';
import { NullPromise } from '../../../webmodule-common/interop/webmodule-interop';
import { startSessionKeepAlive, stopSessionKeepAlive } from '../../../webmodule-common/other/api/session-keep-alive';
import { SupplierSettings } from '../../api/supplier-api-interface-supplier';
import { SupplierSettingsManager } from './supplier-settings-manager';

/**
 * Named supplier feature flags. Keep in sync with the backend `SupplierFeatureNames`.
 */
export const supplierFeatureNames = {
  fabricator: 'fabricator'
} as const;

export class CurrentUserDataStore {
  private static instance: CurrentUserDataStore;
  loaded = false;
  public supplierSettingsManager: SupplierSettingsManager = new SupplierSettingsManager();
  private loadCorePromise = new MultiPromise<void>(async () => await this.performLoadCoreDetails(), false);

  private constructor() {}

  private _allFranchiseeDetails: ResultGetAllFranchiseeDetails | null = null;

  public get allFranchiseeDetails(): ResultGetAllFranchiseeDetails {
    this.checkLoaded();
    if (!this._allFranchiseeDetails) throw new DevelopmentError('Franchisee Summary is null');
    return this._allFranchiseeDetails;
  }

  private _globalSupplier: GlobalSupplierReference | null = null;

  public get globalSupplier(): GlobalSupplierReference {
    this.checkLoaded();
    if (!this._globalSupplier) throw new DevelopmentError('GlobalSupplierReference is null');
    return this._globalSupplier;
  }

  private _features: { [key: string]: boolean } = {};

  /**
   * Named supplier feature entitlements, surfaced to the portal via getActiveUserInformation
   * and sourced from the supplier's GlobalSupplier parameters on the deployment configuration.
   * A missing (or false) key means the feature is not enabled. Prefer adding gates here over
   * per-feature flags so new features don't reshape the API. Keep names in sync with the
   * backend SupplierFeatureNames.
   */
  public isFeatureEnabled(feature: string): boolean {
    this.checkLoaded();
    return this._features[feature] === true;
  }

  /**
   * Convenience gate for the fabricator workflow. Entitled when the supplier's
   * "enable-fabricator-features" parameter is "yes" (case-insensitive); otherwise the
   * not-yet-released feature stays hidden. Gates whether onboarding offers the
   * "Enable Fabricator Workflow" selection.
   */
  public get isFabricatorFeaturesEnabled(): boolean {
    return this.isFeatureEnabled(supplierFeatureNames.fabricator);
  }

  public get supplierSettings(): SupplierSettings {
    this.checkLoaded();
    this.supplierSettingsManager.checkLoaded();
    return this.supplierSettingsManager.supplierSettings;
  }

  public static getInstance(): CurrentUserDataStore {
    if (!CurrentUserDataStore.instance) {
      CurrentUserDataStore.instance = new CurrentUserDataStore();
    }
    return CurrentUserDataStore.instance;
  }

  /**
   * This should only be called from LogOFF
   */
  clear() {
    this.loaded = false;
    this._allFranchiseeDetails = null;
    this._globalSupplier = null;
    this._features = {};
    this.supplierSettingsManager.clear();
    this.loadCorePromise.reset();

    // Stop the session keep-alive timer
    stopSessionKeepAlive();

    flushCache();
  }

  /**
   * This should called so the next await gets new data, without destroying in use data
   */
  requestRefresh() {
    this.loadCorePromise.reset();
  }

  /**
   * forces the cache to refresh
   * @returns a fresh copy of the franchisee
   */
  async reLoadGlobalSupplier(): NullPromise<GlobalSupplierReference> {
    this.requestRefresh();
    await this.loadCoreDetails();
    return this.globalSupplier;
  }

  async loadCoreDetailsAfterLogin() {
    await this.loadCorePromise.run();
    startSessionKeepAlive();
  }

  async loadCoreDetails() {
    await this.loadCorePromise.run();
  }

  async reloadFranchiseeDetails() {
    this._allFranchiseeDetails = this.excludeDealerConnectTenants(
      await getApiFactory().franchisee().getAllFranchiseeDetails()
    );
  }

  private checkLoaded() {
    if (!this.loaded) throw new Error('CurrentUserDataStore has not been loaded');
  }

  /**
   * DealerConnect tenants should not appear anywhere in the supplier portal's franchisee/branch
   * UI (network view, regions/territory, dashboard filters, etc). Every one of those views reads
   * franchisee data through this store, so excluding them once here - rather than in each view -
   * is what keeps that exclusion from being forgotten in a new consumer.
   */
  private excludeDealerConnectTenants(
    result: ResultGetAllFranchiseeDetails | null
  ): ResultGetAllFranchiseeDetails | null {
    if (!result) return result;
    const tenants = result.tenantList.tenants ?? [];
    result.tenantList.deployments?.forEach(deployment => {
      deployment.tenants =
        deployment.tenants?.filter(t => {
          const tenant = tenants.find(x => x.id === t.tenantId);
          return tenant?.claims?.[claimIdentity.dealerConnectUser] !== 'true';
        }) ?? null;
    });
    return result;
  }

  private async performLoadCoreDetails() {
    const user = getCurrentUser();
    if (!user) throw new DevelopmentError('Authentication could not be performed');

    const api = getApiFactory().franchisee();

    const result = await api.getActiveUserInformation({
      systemUserId: user.id
    });
    if (result) {
      this._globalSupplier = result.globalSupplier;
      this._features = result.features ?? {};
      setUserClaims(result.userClaims);
    } else {
      // Result is null - this means the API layer already handled the error
      // (e.g., 412 duplicate login shows dialog and logs out via responseHandler).
      // Don't throw - just return and let the existing error handling complete.
      return;
    }

    if (!(await this.supplierSettingsManager.needsSettings())) throw new Error('Could not load supplier settings');

    this._allFranchiseeDetails = this.excludeDealerConnectTenants(await api.getAllFranchiseeDetails());
    this.loaded = true;
  }
}

export const userDataStore = CurrentUserDataStore.getInstance();
